import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  PenSquare,
  Inbox,
  Send,
  FileText,
  Trash2,
  Star,
  Loader2,
  ChevronLeft,
  Mail,
  MailOpen,
  Search,
  X,
  Clock,
  AlertCircle,
  CheckCircle2,
  Settings,
  Plus,
  Check,
  Paperclip,
  Download,
  Menu,
} from "lucide-react";
import { useCMS } from "@/context/CMSContext";
import type { Email, EmailAttachment } from "@/context/CMSContext";
import TiptapEditor from "@/components/ui/TiptapEditor";
import { useAdminAuth } from "@/context/AdminAuthContext";

type EmailView = "inbox" | "starred" | "sent" | "drafts" | "trash" | "compose" | "detail" | "settings";

const FOLDER_LABELS: Record<string, string> = {
  inbox: "Inbox",
  starred: "Starred",
  sent: "Sent",
  drafts: "Drafts",
  trash: "Trash",
  settings: "Settings",
};

export default function AdminEmail() {
  const { emails, emailIdentities, refreshEmails, refreshEmailIdentities, addEmail, updateEmail, deleteEmail, addEmailIdentity, updateEmailIdentity, deleteEmailIdentity, isLoading } = useCMS();
  const { user, hasRole } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canManageEmailSettings = hasRole(["super-admin"]);
  const usesPersonalMailException = user?.email?.toLowerCase() === "system@bomtrackng.com";
  const personalMail = (!canManageEmailSettings || usesPersonalMailException) ? user?.personal_email || "" : "";
  const defaultIdentityId = canManageEmailSettings
    ? emailIdentities.find(i => i.is_default)?.id || emailIdentities[0]?.id || 0
    : 0;
  const [activeView, setActiveView] = useState<EmailView>("inbox");
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Compose form state
  const [composeForm, setComposeForm] = useState({
    identity_id: 0,
    to: "",
    subject: "",
    body: "",
    html_body: "",
    attachments: [] as { filename: string; content: string }[],
  });

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    email: "",
    name: "",
    is_default: false,
  });
  const [isSavingIdentity, setIsSavingIdentity] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Load emails when view changes
  useEffect(() => {
    if (activeView === "inbox" || activeView === "starred" || activeView === "sent" || activeView === "drafts" || activeView === "trash") {
      handleRefresh(activeView === "starred" ? "starred" : activeView);
    }
  }, [activeView]);

  // Load identities on mount
  useEffect(() => {
    refreshEmailIdentities();
  }, []);

  useEffect(() => {
    if (searchParams.get("compose") !== "1") return;

    const to = searchParams.get("to") || "";
    const subject = searchParams.get("subject") || "";

    setComposeForm({
      identity_id: defaultIdentityId,
      to,
      subject,
      body: "",
      html_body: "",
      attachments: [],
    });
    setSelectedEmail(null);
    setSendStatus(null);
    setActiveView("compose");
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, defaultIdentityId]);

  useEffect(() => {
    if (activeView === "settings" && !canManageEmailSettings) {
      setActiveView("inbox");
    }
  }, [activeView, canManageEmailSettings]);

  const handleRefresh = async (folder?: string) => {
    setIsRefreshing(true);
    await refreshEmails(folder);
    setIsRefreshing(false);
  };

  // Filter emails by folder
  const getFolderEmails = () => {
    let filtered = emails;
    if (activeView === "inbox") filtered = emails.filter((e) => e.folder === "inbox");
    else if (activeView === "starred") filtered = emails.filter((e) => e.starred);
    else if (activeView === "sent") filtered = emails.filter((e) => e.folder === "sent");
    else if (activeView === "drafts") filtered = emails.filter((e) => e.folder === "drafts");
    else if (activeView === "trash") filtered = emails.filter((e) => e.folder === "trash");

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.subject.toLowerCase().includes(q) ||
          e.body.toLowerCase().includes(q) ||
          e.from_name?.toLowerCase().includes(q) ||
          e.to_email.toLowerCase().includes(q)
      );
    }
    return filtered;
  };

  const folderEmails = getFolderEmails();

  // Counts for sidebar badges
  const counts = {
    inbox: emails.filter((e) => e.folder === "inbox" && !e.read).length,
    starred: emails.filter((e) => e.starred).length,
    sent: emails.filter((e) => e.folder === "sent").length,
    drafts: emails.filter((e) => e.folder === "drafts").length,
    trash: emails.filter((e) => e.folder === "trash").length,
  };

  // Open email detail
  const openEmail = async (email: Email) => {
    setSelectedEmail(email);
    setActiveView("detail");
    if (!email.read && email.folder === "inbox") {
      await updateEmail(email.id, { read: true });
    }
  };

  // Toggle star
  const toggleStar = async (e: React.MouseEvent, email: Email) => {
    e.stopPropagation();
    await updateEmail(email.id, { starred: !email.starred });
  };

  // Move to trash
  const moveToTrash = async (e: React.MouseEvent, email: Email) => {
    e.stopPropagation();
    if (email.folder === "trash") {
      await deleteEmail(email.id);
    } else {
      await updateEmail(email.id, { folder: "trash" });
    }
  };

  // Restore from trash
  const restoreEmail = async (e: React.MouseEvent, email: Email) => {
    e.stopPropagation();
    await updateEmail(email.id, { folder: "inbox" });
  };

  // Send email
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!composeForm.to || !composeForm.subject) return;

    setIsSending(true);
    setSendStatus(null);
    try {
      await addEmail({
        identity_id: composeForm.identity_id || undefined,
        from_name: "",
        from_email: "",
        to_email: composeForm.to,
        subject: composeForm.subject,
        body: composeForm.body,
        html_body: composeForm.html_body,
        attachments: composeForm.attachments,
        folder: "sent",
        starred: false,
        read: true,
        send: true,
      });
      setSendStatus({ type: "success", message: "Email sent successfully!" });
      setComposeForm({ identity_id: 0, to: "", subject: "", body: "", html_body: "", attachments: [] });
      setTimeout(() => {
        setSendStatus(null);
        setActiveView("sent");
      }, 1500);
    } catch (error) {
      setSendStatus({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to send email",
      });
    } finally {
      setIsSending(false);
    }
  };

  // Save draft
  const handleSaveDraft = async () => {
    if (!composeForm.to && !composeForm.subject && !composeForm.body) return;
    try {
      await addEmail({
        identity_id: composeForm.identity_id || undefined,
        from_name: "",
        from_email: "",
        to_email: composeForm.to,
        subject: composeForm.subject || "(No subject)",
        body: composeForm.body,
        html_body: composeForm.html_body,
        attachments: composeForm.attachments,
        folder: "drafts",
        starred: false,
        read: true,
      });
      setSendStatus({ type: "success", message: "Draft saved!" });
      setComposeForm({ identity_id: 0, to: "", subject: "", body: "", html_body: "", attachments: [] });
      setTimeout(() => {
        setSendStatus(null);
        setActiveView("drafts");
      }, 1200);
    } catch (error) {
      setSendStatus({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to save draft",
      });
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      if (days === 0) return date.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" });
      if (days === 1) return "Yesterday";
      if (days < 7) return date.toLocaleDateString("en-NG", { weekday: "short" });
      return date.toLocaleDateString("en-NG", { month: "short", day: "numeric" });
    } catch {
      return dateString;
    }
  };

  const formatFullDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString("en-NG", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const avatarColors = ["#3b82f6", "#8b5cf6", "#ef4444", "#10b981", "#f59e0b", "#ec4899"];
  const getAvatarColor = (id: number) => avatarColors[(id || 0) % avatarColors.length];

  const sidebarItems: { id: EmailView; label: string; icon: React.ElementType; count?: number }[] = [
    { id: "inbox", label: "Inbox", icon: Inbox, count: counts.inbox },
    { id: "starred", label: "Starred", icon: Star, count: counts.starred },
    { id: "sent", label: "Sent", icon: Send, count: counts.sent },
    { id: "drafts", label: "Drafts", icon: FileText, count: counts.drafts },
    { id: "trash", label: "Trash", icon: Trash2, count: counts.trash },
    ...(canManageEmailSettings ? [{ id: "settings" as EmailView, label: "Settings", icon: Settings }] : []),
  ];
  const activeSidebarItem = sidebarItems.find((item) => item.id === activeView);

  return (
    <div className="flex h-[calc(100vh-120px)] min-h-[500px] bg-white rounded-xl border border-neutral-100 overflow-hidden">
      {/* Mini Sidebar */}
      <aside className="hidden md:flex w-56 bg-neutral-50 border-r border-neutral-100 flex-col flex-shrink-0">
        {/* Compose Button */}
        <div className="p-4">
          <button
            onClick={() => {
              setActiveView("compose");
              setSelectedEmail(null);
              setSendStatus(null);
            }}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-xl text-sm font-medium transition-colors shadow-lg shadow-accent/20"
          >
            <PenSquare className="w-4 h-4" />
            Compose
          </button>
        </div>

        {/* Folder Navigation */}
        <nav className="flex-1 px-2 space-y-0.5">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveView(item.id);
                setSelectedEmail(null);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                activeView === item.id
                  ? "bg-white text-primary shadow-sm border border-neutral-100 font-medium"
                  : "text-neutral-500 hover:bg-white/60 hover:text-primary"
              }`}
            >
              <item.icon className={`w-4 h-4 ${activeView === item.id ? "text-accent" : ""}`} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.count ? (
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    item.id === "inbox" && item.count > 0
                      ? "bg-accent/10 text-accent"
                      : "bg-neutral-200 text-neutral-500"
                  }`}
                >
                  {item.count}
                </span>
              ) : null}
            </button>
          ))}
        </nav>

        {/* Storage hint */}
        <div className="p-4 border-t border-neutral-100">
          <div className="flex items-center gap-2 text-xs text-neutral-400">
            <Mail className="w-3.5 h-3.5" />
            <span>{emails.length} emails</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile Mailbox Navigation */}
        <div className="md:hidden border-b border-neutral-100 bg-neutral-50">
          <div className="p-3 flex items-center gap-2">
            <button
              onClick={() => {
                setActiveView("compose");
                setSelectedEmail(null);
                setSendStatus(null);
                setMobileNavOpen(false);
              }}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-xl text-sm font-medium transition-colors shadow-lg shadow-accent/20"
            >
              <PenSquare className="w-4 h-4" />
              Compose
            </button>
            <button
              onClick={() => setMobileNavOpen((open) => !open)}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-neutral-200 bg-white text-primary text-sm font-medium"
            >
              {mobileNavOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              <span>{activeSidebarItem?.label || "Folders"}</span>
            </button>
          </div>

          {mobileNavOpen && (
            <nav className="px-3 pb-3 grid grid-cols-2 gap-2">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveView(item.id);
                    setSelectedEmail(null);
                    setMobileNavOpen(false);
                  }}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                    activeView === item.id
                      ? "bg-white text-primary shadow-sm border border-neutral-100 font-medium"
                      : "bg-white/60 text-neutral-500 hover:bg-white hover:text-primary"
                  }`}
                >
                  <item.icon className={`w-4 h-4 ${activeView === item.id ? "text-accent" : ""}`} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.count ? (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        item.id === "inbox" && item.count > 0
                          ? "bg-accent/10 text-accent"
                          : "bg-neutral-200 text-neutral-500"
                      }`}
                    >
                      {item.count}
                    </span>
                  ) : null}
                </button>
              ))}
            </nav>
          )}
        </div>

        <AnimatePresence mode="wait">
          {/* Email List Views */}
          {activeView !== "compose" && activeView !== "detail" && activeView !== "settings" && (
            <motion.div
              key={activeView}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col h-full"
            >
              {/* Toolbar */}
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between px-3 md:px-5 py-3 border-b border-neutral-100">
                <h2 className="text-sm font-semibold text-primary">{FOLDER_LABELS[activeView] || "Emails"}</h2>
                <div className="flex items-center gap-2">
                  {/* Search */}
                  <div className="relative flex-1 md:flex-none">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
                    <input
                      type="text"
                      placeholder="Search emails..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8 pr-8 py-1.5 rounded-lg border border-neutral-200 text-sm w-full md:w-48 focus:border-accent focus:ring-1 focus:ring-accent/20 outline-none"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                      >
                        <X className="w-3.5 h-3.5 text-neutral-400 hover:text-neutral-600" />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => handleRefresh(activeView === "starred" ? "starred" : activeView)}
                    disabled={isRefreshing}
                    className="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
                    title="Refresh"
                  >
                    <Loader2 className={`w-4 h-4 text-neutral-500 ${isRefreshing ? "animate-spin" : ""}`} />
                  </button>
                </div>
              </div>

              {/* Email List */}
              <div className="flex-1 overflow-y-auto">
                {isLoading && emails.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 text-accent animate-spin" />
                  </div>
                ) : folderEmails.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-neutral-400">
                    <MailOpen className="w-12 h-12 mb-3 text-neutral-200" />
                    <p className="text-sm">No emails in {FOLDER_LABELS[activeView]?.toLowerCase()}</p>
                    {activeView === "inbox" && (
                      <button
                        onClick={() => setActiveView("compose")}
                        className="mt-3 text-accent text-sm hover:underline"
                      >
                        Compose a new email
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-neutral-50">
                    {folderEmails.map((email) => (
                      <div
                        key={email.id}
                        onClick={() => openEmail(email)}
                        className={`group flex items-start gap-3 px-5 py-3.5 hover:bg-neutral-50 cursor-pointer transition-colors ${
                          !email.read && email.folder === "inbox" ? "bg-blue-50/30" : ""
                        }`}
                      >
                        {/* Star */}
                        <button
                          onClick={(e) => toggleStar(e, email)}
                          className="mt-0.5 flex-shrink-0"
                        >
                          <Star
                            className={`w-4 h-4 transition-colors ${
                              email.starred
                                ? "text-yellow-400 fill-yellow-400"
                                : "text-neutral-300 hover:text-neutral-400"
                            }`}
                          />
                        </button>

                        {/* Avatar */}
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                          style={{ backgroundColor: getAvatarColor(email.id) }}
                        >
                          {getInitials(email.from_name || email.to_email)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className={`text-sm truncate ${!email.read && email.folder === "inbox" ? "font-semibold text-primary" : "text-neutral-700"}`}>
                              {email.folder === "sent" ? `To: ${email.to_email}` : email.from_name || email.from_email || email.to_email}
                            </p>
                            {!email.read && email.folder === "inbox" && (
                              <span className="w-2 h-2 bg-accent rounded-full flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <p className={`text-sm truncate ${!email.read && email.folder === "inbox" ? "font-medium text-primary" : "text-neutral-600"}`}>
                              {email.subject}
                            </p>
                            {email.attachments && email.attachments.length > 0 && (
                              <Paperclip className="w-3 h-3 text-neutral-400 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-neutral-400 truncate mt-0.5">
                            {(email.body || email.html_body?.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || "").slice(0, 80)}
                            {((email.body?.length || 0) > 80 || (email.html_body?.length || 0) > 80) ? "..." : ""}
                          </p>
                        </div>

                        {/* Date + Actions */}
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <span className="text-xs text-neutral-400">{formatDate(email.created_at)}</span>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {email.folder === "trash" ? (
                              <button
                                onClick={(e) => restoreEmail(e, email)}
                                className="p-1.5 rounded-lg hover:bg-neutral-200 transition-colors"
                                title="Restore"
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                              </button>
                            ) : null}
                            <button
                              onClick={(e) => moveToTrash(e, email)}
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                              title={email.folder === "trash" ? "Delete permanently" : "Move to trash"}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Compose View */}
          {activeView === "compose" && (
            <motion.div
              key="compose"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col h-full"
            >
              {/* Compose Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
                <h2 className="text-sm font-semibold text-primary">New Message</h2>
                <button
                  onClick={() => {
                    setActiveView("inbox");
                    setSendStatus(null);
                  }}
                  className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
                >
                  <X className="w-4 h-4 text-neutral-500" />
                </button>
              </div>

              {/* Compose Form */}
              <div className="flex-1 overflow-y-auto p-5">
                {sendStatus && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mb-4 p-3 rounded-xl flex items-center gap-2 text-sm ${
                      sendStatus.type === "success"
                        ? "bg-green-50 text-green-600 border border-green-100"
                        : "bg-red-50 text-red-600 border border-red-100"
                    }`}
                  >
                    {sendStatus.type === "success" ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                    {sendStatus.message}
                  </motion.div>
                )}

                <form onSubmit={handleSend} className="space-y-4 max-w-2xl">
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1.5">From</label>
                    <select
                      value={composeForm.identity_id}
                      onChange={(e) => setComposeForm((p) => ({ ...p, identity_id: parseInt(e.target.value) || 0 }))}
                      className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm bg-white"
                    >
                      {personalMail ? (
                        <option value={0}>{user?.name || "Admin"} &lt;{personalMail}&gt;</option>
                      ) : (
                        emailIdentities.map((identity) => (
                          <option key={identity.id} value={identity.id}>
                            {identity.name} &lt;{identity.email}&gt;
                            {identity.is_default ? ' (Default)' : ''}
                          </option>
                        ))
                      )}
                      {!personalMail && emailIdentities.length === 0 && (
                        <option value={0}>info@bomtrackng.com</option>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1.5">To</label>
                    <input
                      type="email"
                      required
                      value={composeForm.to}
                      onChange={(e) => setComposeForm((p) => ({ ...p, to: e.target.value }))}
                      placeholder="recipient@example.com"
                      className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1.5">Subject</label>
                    <input
                      type="text"
                      required
                      value={composeForm.subject}
                      onChange={(e) => setComposeForm((p) => ({ ...p, subject: e.target.value }))}
                      placeholder="Email subject"
                      className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                    />
                  </div>

                  <div className="flex-1">
                    <label className="block text-xs font-medium text-neutral-500 mb-1.5">Message</label>
                    <TiptapEditor
                      content={composeForm.html_body}
                      onChange={(html) => {
                        // Extract plain text for body field
                        const plainText = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                        setComposeForm((p) => ({ ...p, html_body: html, body: plainText }));
                      }}
                      placeholder="Write your message here..."
                    />
                  </div>

                  {/* Attachments */}
                  {composeForm.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {composeForm.attachments.map((att, i) => (
                        <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-neutral-100 rounded-lg text-xs text-neutral-600">
                          <Paperclip className="w-3 h-3" />
                          {att.filename}
                          <button
                            type="button"
                            onClick={() => setComposeForm((p) => ({
                              ...p,
                              attachments: p.attachments.filter((_, idx) => idx !== i)
                            }))}
                            className="ml-1 text-neutral-400 hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={isSending || !composeForm.to || !composeForm.subject}
                      className="px-5 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-xl text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition-colors shadow-lg shadow-accent/20"
                    >
                      {isSending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={isSending}
                      className="px-5 py-2.5 border border-neutral-200 text-neutral-600 hover:bg-neutral-50 rounded-xl text-sm font-medium transition-colors"
                    >
                      Save Draft
                    </button>
                    <label className="px-5 py-2.5 border border-neutral-200 text-neutral-600 hover:bg-neutral-50 rounded-xl text-sm font-medium transition-colors cursor-pointer flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      Attach
                      <input
                        type="file"
                        className="hidden"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          files.forEach((file) => {
                            const reader = new FileReader();
                            reader.onload = (event) => {
                              const base64 = (event.target?.result as string).split(',')[1];
                              setComposeForm((p) => ({
                                ...p,
                                attachments: [...p.attachments, { filename: file.name, content: base64 }]
                              }));
                            };
                            reader.readAsDataURL(file);
                          });
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setComposeForm({ identity_id: 0, to: "", subject: "", body: "", html_body: "", attachments: [] });
                        setSendStatus(null);
                      }}
                      className="px-5 py-2.5 text-neutral-400 hover:text-neutral-600 text-sm font-medium transition-colors"
                    >
                      Discard
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}

          {/* Detail View */}
          {activeView === "detail" && selectedEmail && (
            <motion.div
              key="detail"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className="flex flex-col h-full"
            >
              {/* Detail Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setActiveView(selectedEmail.folder === "trash" ? "trash" : selectedEmail.folder === "sent" ? "sent" : "inbox");
                      setSelectedEmail(null);
                    }}
                    className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4 text-neutral-500" />
                  </button>
                  <h2 className="text-sm font-semibold text-primary">Message</h2>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleStar({ stopPropagation: () => {} } as React.MouseEvent, selectedEmail)}
                    className="p-2 rounded-lg hover:bg-neutral-100 transition-colors"
                  >
                    <Star
                      className={`w-4 h-4 ${
                        selectedEmail.starred ? "text-yellow-400 fill-yellow-400" : "text-neutral-300"
                      }`}
                    />
                  </button>
                  <button
                    onClick={() => moveToTrash({ stopPropagation: () => {} } as React.MouseEvent, selectedEmail)}
                    className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>

              {/* Email Content */}
              <div className="flex-1 overflow-y-auto p-5">
                <div className="max-w-3xl">
                  <h1 className="text-xl font-semibold text-primary mb-4">{selectedEmail.subject}</h1>

                  <div className="flex items-start gap-3 mb-6 pb-6 border-b border-neutral-100">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                      style={{ backgroundColor: getAvatarColor(selectedEmail.id) }}
                    >
                      {getInitials(selectedEmail.from_name || selectedEmail.to_email)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-primary">
                            {selectedEmail.from_name || selectedEmail.from_email}
                          </p>
                          <p className="text-xs text-neutral-400">
                            {selectedEmail.folder === "sent" ? `To: ${selectedEmail.to_email}` : selectedEmail.from_email}
                          </p>
                        </div>
                        <span className="text-xs text-neutral-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatFullDate(selectedEmail.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Attachments */}
                  {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {selectedEmail.attachments.map((att, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-neutral-100 hover:bg-neutral-200 rounded-lg text-xs text-neutral-600 cursor-pointer transition-colors"
                          title={att.content_type || 'Attachment'}
                        >
                          <Paperclip className="w-3 h-3" />
                          {att.filename}
                          {att.size && ` (${(att.size / 1024).toFixed(1)} KB)`}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Email Body */}
                  {selectedEmail.html_body ? (
                    <div
                      className="text-neutral-700 leading-relaxed email-content"
                      style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '14px', lineHeight: '1.6' }}
                      dangerouslySetInnerHTML={{ __html: selectedEmail.html_body }}
                    />
                  ) : (
                    <div className="text-neutral-700 leading-relaxed whitespace-pre-wrap" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '14px', lineHeight: '1.6' }}>
                      {selectedEmail.body}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-3 mt-8 pt-6 border-t border-neutral-100">
                    <button
                      onClick={() => {
                        setComposeForm({
                          identity_id: defaultIdentityId,
                          to: selectedEmail.folder === "sent" ? selectedEmail.to_email : selectedEmail.from_email,
                          subject: `Re: ${selectedEmail.subject}`,
                          body: `\n\n--- On ${formatFullDate(selectedEmail.created_at)}, ${selectedEmail.from_name || selectedEmail.from_email} wrote ---\n${selectedEmail.body}`,
                          html_body: `<br><br>--- On ${formatFullDate(selectedEmail.created_at)}, ${selectedEmail.from_name || selectedEmail.from_email} wrote ---<br>${selectedEmail.html_body || selectedEmail.body.replace(/\n/g, '<br>')}`,
                          attachments: [],
                        });
                        setActiveView("compose");
                        setSelectedEmail(null);
                      }}
                      className="px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-xl text-sm font-medium transition-colors"
                    >
                      Reply
                    </button>
                    {selectedEmail.folder === "drafts" && (
                      <button
                        onClick={() => {
                          setComposeForm({
                            identity_id: defaultIdentityId,
                            to: selectedEmail.to_email,
                            subject: selectedEmail.subject,
                            body: selectedEmail.body,
                            html_body: selectedEmail.html_body || "",
                            attachments: selectedEmail.attachments?.map((a: EmailAttachment) => ({
                              filename: a.filename,
                              content: a.content || "",
                            })) || [],
                          });
                          setActiveView("compose");
                          setSelectedEmail(null);
                        }}
                        className="px-4 py-2 border border-neutral-200 text-neutral-600 hover:bg-neutral-50 rounded-xl text-sm font-medium transition-colors"
                      >
                        Edit Draft
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Settings View */}
          {activeView === "settings" && canManageEmailSettings && (
            <motion.div
              key="settings"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col h-full"
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
                <h2 className="text-sm font-semibold text-primary">Email Settings</h2>
              </div>

              <div className="flex-1 overflow-y-auto p-5">
                <div className="max-w-xl space-y-6">
                  {/* Add Identity Form */}
                  <div className="bg-neutral-50 rounded-xl p-5 border border-neutral-100">
                    <h3 className="text-sm font-semibold text-primary mb-4">Add Send-From Address</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-neutral-500 mb-1.5">Display Name</label>
                        <input
                          type="text"
                          value={settingsForm.name}
                          onChange={(e) => setSettingsForm((p) => ({ ...p, name: e.target.value }))}
                          placeholder="Bomtrack Nigeria Limited"
                          className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-neutral-500 mb-1.5">Email Address</label>
                        <input
                          type="email"
                          value={settingsForm.email}
                          onChange={(e) => setSettingsForm((p) => ({ ...p, email: e.target.value }))}
                          placeholder="info@bomtrackng.com"
                          className="w-full px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
                        />
                      </div>
                      <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settingsForm.is_default}
                          onChange={(e) => setSettingsForm((p) => ({ ...p, is_default: e.target.checked }))}
                          className="rounded border-neutral-300 text-accent focus:ring-accent"
                        />
                        Set as default
                      </label>
                      <button
                        onClick={async () => {
                          if (!settingsForm.email || !settingsForm.name) return;
                          setIsSavingIdentity(true);
                          try {
                            await addEmailIdentity({
                              email: settingsForm.email,
                              name: settingsForm.name,
                              is_default: settingsForm.is_default,
                            });
                            setSettingsForm({ email: "", name: "", is_default: false });
                          } catch (err) {
                            alert(err instanceof Error ? err.message : "Failed to add identity");
                          } finally {
                            setIsSavingIdentity(false);
                          }
                        }}
                        disabled={isSavingIdentity || !settingsForm.email || !settingsForm.name}
                        className="px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-xl text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
                      >
                        {isSavingIdentity ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        Add Identity
                      </button>
                    </div>
                  </div>

                  {/* Identities List */}
                  <div>
                    <h3 className="text-sm font-semibold text-primary mb-3">Send-From Addresses</h3>
                    <div className="divide-y divide-neutral-100 border border-neutral-100 rounded-xl overflow-hidden">
                      {emailIdentities.map((identity) => (
                        <div key={identity.id} className="flex items-center justify-between px-4 py-3 bg-white">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-accent/10 text-accent flex items-center justify-center text-xs font-semibold">
                              {identity.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-primary">{identity.name}</p>
                              <p className="text-xs text-neutral-400">{identity.email}</p>
                            </div>
                            {identity.is_default && (
                              <span className="text-[10px] px-2 py-0.5 bg-green-50 text-green-600 rounded-full font-medium">
                                Default
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {!identity.is_default && (
                              <button
                                onClick={() => updateEmailIdentity(identity.id, { is_default: true })}
                                className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
                                title="Set as default"
                              >
                                <Check className="w-3.5 h-3.5 text-neutral-400" />
                              </button>
                            )}
                            <button
                              onClick={() => {
                                if (confirm(`Delete ${identity.email}?`)) {
                                  deleteEmailIdentity(identity.id);
                                }
                              }}
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        </div>
                      ))}
                      {emailIdentities.length === 0 && (
                        <div className="px-4 py-6 text-center text-neutral-400 text-sm">
                          No identities configured. Add one above.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Troubleshooting */}
                  <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                    <h3 className="text-sm font-semibold text-blue-800 mb-2">Inbound Email Not Working?</h3>
                    <ul className="text-xs text-blue-700 space-y-1.5 list-disc list-inside">
                      <li>Verify <code className="bg-blue-100 px-1 rounded">bomtrackng.com</code> in Resend Dashboard</li>
                      <li>Add DNS records (MX, SPF, DKIM) to Cloudflare</li>
                      <li>Set webhook URL: <code className="bg-blue-100 px-1 rounded">https://www.bomtrackng.com/api/email/inbound</code></li>
                      <li>Check Cloudflare env vars: <code className="bg-blue-100 px-1 rounded">RESEND_API_KEY</code>, <code className="bg-blue-100 px-1 rounded">NOTIFICATION_EMAIL</code></li>
                      <li>Test with: POST to <code className="bg-blue-100 px-1 rounded">/api/email/test-inbound</code></li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
